import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { A11yModule } from '@angular/cdk/a11y';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';

import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormlySelectOption, FormlySelectOptionsPipe } from '@ngx-formly/core/select';

import { ContentContainerComponent } from '@shared/preloader';
import { GenericButtonComponent } from '@shared/ui';
import { SingleSelectStore, DEFAULT_VISIBLE_OPTIONS_COUNT } from './single-select.store';

@Component({
  selector: 'fc-single-select',
  templateUrl: './single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: '../generic-select.component.scss',
  providers: [SingleSelectStore, FormlySelectOptionsPipe],
  imports: [
    A11yModule,
    ContentContainerComponent,
    FormsModule,
    GenericButtonComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatTooltipModule,
    NgIf,
    ReactiveFormsModule,
  ],
})
export class SingleSelectComponent implements OnInit {
  readonly field = input<FormlyFieldConfig>();

  protected readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(SingleSelectStore);
  protected readonly formlySelectOptionsPipe = inject(FormlySelectOptionsPipe);
  protected readonly DEFAULT_VISIBLE_OPTIONS_COUNT = DEFAULT_VISIBLE_OPTIONS_COUNT;
  protected readonly emptyOption: FormlySelectOption = { label: 'None', value: null };

  ngOnInit(): void {
    const f = this.field();

    if (typeof f?.props?.['visibleOptionsCount'] === 'number') {
      this.store.setInitialVisibleOptionsCount(f?.props?.['visibleOptionsCount']);
    }

    this.store.setOptions(
      this.formlySelectOptionsPipe.transform(f?.props?.options ?? [], f),
      f?.formControl?.value ?? [],
    );

    f?.formControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(values => {
      if (values) {
        this.store.toggleOption(values);
      } else {
        this.store.toggleOption(null);
      }
    });
  }
}
